import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { UseMutationResult } from "@tanstack/react-query";

import { API_ROUTES } from "../../api/ApiRoutes";
import {
  TASK_BET_TYPE,
  TASK_CASINO_ACTION,
  TASK_DATA_SOURCE,
  TASK_OUTCOME_REQUIRED,
  TASK_RULE_FIELD,
  TASK_RULE_OP,
} from "../../types/gamePass";
import type {
  CreateTaskRequest,
  CreateTaskResponse,
  SeasonTaskSubmissionMode,
  TaskApiRecord,
  TaskBetType,
  TaskCasinoAction,
  TaskDataSource,
  TaskOutcomeRequired,
  TaskRule,
  TaskRuleField,
} from "../../types/gamePass";
import type {
  BetCurrency,
  BetType,
  CasinoAction,
  SocialTaskType,
  TaskDraft,
  TaskQualifier,
  TaskType,
} from "../../components/GamePass/CreateSeason/createSeasonData";
import {
  BET_CURRENCY_TO_MONEY_TYPE,
  DEFAULT_SOCIAL_TASK_TYPE,
  betTypeRequiresLegCount,
  resolveTaskCompletionMode,
  TASK_COMPLETION_MODE,
} from "../../components/GamePass/CreateSeason/createSeasonData";
import { getPrivateInstance } from "../_privateInstance";
import { unwrapGamePassEntityOrNull } from "./responseMappers";

// NOTE: unlike `useCreateSeason`, we deliberately do NOT inject an
// `operator_id` here. The `gp_tasks` table doesn't carry that column
// (the operator association is transitive: task → challenge → season →
// operator), and the BE rejects the request with
// `Unknown gp_tasks column(s): {'operator_id'}` when we send it.

/** Qualifier pill (`WIN`/`WIN_OR_PUSH`/`ANY_SETTLED`) → BE
 *  `outcome_required` enum. The string values already match the BE
 *  spec verbatim, but the table keeps the contract explicit so a
 *  future relabel of the UI pill doesn't silently flip the wire
 *  value. */
const QUALIFIER_TO_OUTCOME_REQUIRED: Record<TaskQualifier, TaskOutcomeRequired> =
  {
    WIN: TASK_OUTCOME_REQUIRED.WIN,
    WIN_OR_PUSH: TASK_OUTCOME_REQUIRED.WIN_OR_PUSH,
    ANY_SETTLED: TASK_OUTCOME_REQUIRED.ANY_SETTLED,
  };

/** Type-of-bet pill → BE `bet_type` enum. The `"ANY"` UI option maps to
 *  `null` because the BE treats absence as "any bet type" — the example
 *  payload in the spec doc shows `"bet_type": null` for the default
 *  case. Keeping this in a table (rather than a switch) makes the
 *  mapping easy to audit and lets the type checker enforce
 *  exhaustiveness when a new pill is added. */
const BET_TYPE_TO_API_BET_TYPE: Record<BetType, TaskBetType | null> = {
  ANY: null,
  SINGLE: TASK_BET_TYPE.SINGLE,
  PARLAY: TASK_BET_TYPE.PARLAY,
  SGP: TASK_BET_TYPE.SGP,
  LIVE: TASK_BET_TYPE.LIVE,
};

/** Default `target_value` for tasks whose goal count isn't sourced from a
 *  dedicated input, or when that input is blank / invalid. A plain sportsbook
 *  (COUNT) task uses this when its "Number of Bets" input is empty and no
 *  preserved goal exists; socials / streak tasks fall back to it too when their
 *  amount input fails to parse. Guarantees the wire value is never `NaN`. */
const TARGET_VALUE_FALLBACK = 1;

/** Parse the modal's free-text Minimum Odds input into the numeric value the
 *  BE persists on `min_odds_american`. The field is optional and works in
 *  American odds, so the value may be negative (e.g. `-100`); only an empty
 *  / whitespace-only / lone-"-" / non-numeric / exactly-zero input clears it
 *  (`null`). The value round-trips faithfully back into the task preview. */
export const parseOddsRequirement = (value?: string): number | null => {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (trimmed === "" || trimmed === "-") return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) && parsed !== 0 ? parsed : null;
};

/** Parse the modal's free-text XP Amount into the BE `xp_reward` integer.
 *  Optional: a blank / non-numeric / negative input defaults to `0` (no XP).
 *  Floored to an integer since `xp_reward` is a whole-number point value. */
export const parseXpReward = (value?: string): number => {
  if (typeof value !== "string") return 0;
  const trimmed = value.trim();
  if (trimmed === "") return 0;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : 0;
};

/** Map the Number of Legs input onto the BE `min_leg_count`. Only meaningful
 *  for the multi-leg bet types, so callers gate on `betTypeRequiresLegCount`
 *  (Parlay / SGP); a blank / non-numeric / non-positive input clears it
 *  (`null`). Floored to an integer since a leg count is a whole number. */
export const parseMinLegCount = (value?: string): number | null => {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (trimmed === "") return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : null;
};

/** Casino Action pill → BE `outcome` rule value (Play / Win). The Play pill
 *  ships `"settled"` on the wire (FE-2005); Win ships `"WIN"`. The table keeps
 *  the contract explicit so a future relabel of the UI pill doesn't silently
 *  flip the wire value. */
const CASINO_ACTION_TO_API: Record<CasinoAction, TaskCasinoAction> = {
  PLAY: TASK_CASINO_ACTION.PLAY,
  WIN: TASK_CASINO_ACTION.WIN,
};

/** Parse the modal's free-text Casino "Action Amount" into the task's
 *  top-level `target_value`. Optional: a blank / non-numeric / non-positive
 *  input clears it (`null`). Floored to an integer since the value is a
 *  whole-number count of qualifying plays / wins. */
export const parseCasinoActionAmount = (value?: string): number | null => {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (trimmed === "") return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : null;
};

/** Parse the modal's free-text "Minimum Bet Amount" into the BE top-level
 *  `min_bet_amount` value (sportsbook). Optional: a blank / non-numeric /
 *  non-positive input clears it (`null`) — a `0` minimum is "no restriction".
 *  NOT floored: a bet amount is a monetary value and may be fractional (e.g.
 *  `10.5`). */
export const parseBetAmount = (value?: string): number | null => {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (trimmed === "") return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

/** Parse the Socials "X Amount" into the milestone the user must reach — the
 *  BE `target_value` for a social task (e.g. 50 followers, a 10-day streak).
 *  Optional: a blank / non-numeric / non-positive input clears it (`null`),
 *  and the create/update mappers then fall back to `TARGET_VALUE_FALLBACK`.
 *  Floored to an integer since the milestone is a whole-number count. */
export const parseSocialAmount = (value?: string): number | null => {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (trimmed === "") return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : null;
};

/** Parse a free-text whole-number input into a strictly-positive integer, or
 *  `null` for a blank / non-numeric / non-positive value. The single source of
 *  truth for the "positive integer goal" inputs (Streak days, Number of Bets):
 *  both keep their own named export below for call-site clarity, but the
 *  validation lives here so it can't drift between them. */
const parsePositiveInteger = (value?: string): number | null => {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (trimmed === "") return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : null;
};

/** Parse the sportsbook "Streak" condition card into the number of streak days.
 *  When present (a positive integer), the task switches to a streak goal: the
 *  BE wants exactly two fields to drive streak logic — `counting_method:
 *  "STREAK"` and `target_value: <days>` (see `resolveCountingMethod` /
 *  `resolveSportsbookTargetValue`). A blank / non-numeric / non-positive input
 *  returns `null`, leaving the task a plain Number-of-Bets COUNT task. Floored
 *  to an integer since a streak length is a whole number of days. */
export const parseStreakDays = (value?: string): number | null =>
  parsePositiveInteger(value);

/** Parse the sportsbook "Number of Bets" input into the goal count the user
 *  must reach — the BE `target_value` for a plain (COUNT) sportsbook task. A
 *  blank / non-numeric / non-positive input returns `null`, leaving the task to
 *  fall back to a preserved goal or the default (see `resolveTargetValue`).
 *  Floored to an integer since the goal is a whole-number count of bets. */
export const parseNumberOfBets = (value?: string): number | null =>
  parsePositiveInteger(value);

/** Map the Create Task modal's Task Type pill (`TaskType`) onto the BE
 *  `data_source` enum. Identity for sportsbook / casino, but the socials pill
 *  value (`"SOCIALS"`, plural) translates to the singular wire value
 *  (`"SOCIAL"`) — the BE rejects `"SOCIALS"` with a 422. This is the single
 *  source of truth for the UI→wire translation; both mappers use it instead of
 *  shipping `trigger.taskType` verbatim. */
export const TASK_TYPE_TO_DATA_SOURCE: Record<TaskType, TaskDataSource> = {
  SPORTSBOOK: TASK_DATA_SOURCE.SPORTSBOOK,
  SOCIALS: TASK_DATA_SOURCE.SOCIAL,
  CASINO: TASK_DATA_SOURCE.CASINO,
};

/** Whether the draft is a casino task. Casino-only payload fields are sent
 *  as `null` for every other task type so the BE never sees a stale casino
 *  selection bleed in from a task that was switched away from Casino.
 *  Compares the UI Task Type pill value (not the wire `data_source`). */
const isCasinoTask = (draft: TaskDraft): boolean =>
  draft.trigger.taskType === "CASINO";

/** Whether the draft is a socials task. Social tasks carry their own (small)
 *  trigger surface — the Task Type dropdown + "X Amount" — and must NOT ship
 *  the sportsbook bet fields, even if a qualifier / bet-type / odds value
 *  lingers on the draft from before the admin switched the Task Type pill to
 *  Socials (those values are never cleared on switch). Compares the UI Task
 *  Type pill value (`"SOCIALS"`), not the wire `data_source` (`"SOCIAL"`). */
const isSocialTask = (draft: TaskDraft): boolean =>
  draft.trigger.taskType === "SOCIALS";

/** Whether the draft is a sportsbook task — the only task type that ships the
 *  bet-specific fields (`outcome_required`, `bet_type`, odds, `min_leg_count`).
 *  Expressed positively (rather than `!isCasino`) so casino AND social tasks
 *  both fall through to `null`/omission for those fields. */
const isSportsbookTask = (draft: TaskDraft): boolean =>
  draft.trigger.taskType === "SPORTSBOOK";

/** Whether the draft is a sportsbook Streak task — a sportsbook task whose
 *  "Streak" condition card holds a valid (positive) day count. The BE drives
 *  streak logic off two fields only: `counting_method: "STREAK"` and
 *  `target_value: <days>`. */
const isSportsbookStreakTask = (draft: TaskDraft): boolean =>
  isSportsbookTask(draft) && parseStreakDays(draft.trigger.streak) !== null;

/** The BE `counting_method` for a task. A sportsbook Streak task counts a
 *  consecutive-day streak (`"STREAK"`); every other task counts occurrences
 *  (`"COUNT"`) — the constant the create flow has always shipped. Shared by the
 *  create and update mappers so the value flips correctly when a streak is
 *  added to / removed from a task on edit. */
export const resolveCountingMethod = (draft: TaskDraft): string =>
  isSportsbookStreakTask(draft) ? "STREAK" : "COUNT";

/** The BE `target_value` for a task. Each task type sources it from a different
 *  input, so this is the single source of truth shared by the create and update
 *  mappers:
 *    - casino  → the Action Amount (qualifying plays / wins); `null` when blank;
 *    - socials → the "X Amount" milestone (followers, streak length, …),
 *                falling back to `TARGET_VALUE_FALLBACK`;
 *    - sportsbook Streak → the number of streak days (the streak goal);
 *    - sportsbook (default, COUNT) → the resolution order
 *                `parseNumberOfBets(trigger.numberOfBets) ??
 *                trigger.preservedTargetValue ?? TARGET_VALUE_FALLBACK`: the
 *                live "Number of Bets" input first, then the value
 *                `mapApiTaskToDraft` preserved off an existing task (so
 *                re-saving a legacy count-based task with an empty input doesn't
 *                reset its stored goal), then the defensive default. The Minimum
 *                Bet Amount is a separate field that ships on its own
 *                `min_bet_amount` column — it does NOT feed `target_value`.
 *  The socials / streak branches fall back to `TARGET_VALUE_FALLBACK` so the
 *  wire value is never `NaN`. */
export const resolveTargetValue = (draft: TaskDraft): number | null => {
  const trigger = draft.trigger;
  if (isCasinoTask(draft)) {
    return parseCasinoActionAmount(trigger.casinoActionAmount);
  }
  if (isSocialTask(draft)) {
    return parseSocialAmount(trigger.socialAmount) ?? TARGET_VALUE_FALLBACK;
  }
  if (isSportsbookStreakTask(draft)) {
    return parseStreakDays(trigger.streak) ?? TARGET_VALUE_FALLBACK;
  }
  // Plain sportsbook (COUNT): the "Number of Bets" input drives the goal count.
  // Fall back to an existing task's preserved goal on edit (so re-saving a
  // legacy count-based task with an empty input doesn't destroy it), then to
  // the defensive default for freshly authored tasks.
  return (
    parseNumberOfBets(trigger.numberOfBets) ??
    trigger.preservedTargetValue ??
    TARGET_VALUE_FALLBACK
  );
};

/** The BE top-level `min_bet_amount` column (NOT a rule — it lives outside
 *  `rules`). Two task types feed it from their own input:
 *    - sportsbook → the per-currency "Minimum Bet Amount" inputs
 *                   (`trigger.minBetByCurrency`). The BE has a single
 *                   `min_bet_amount` column today, so only a *single* selected
 *                   Bet Currency has an unambiguous value to ship — that
 *                   currency's minimum is sent. When multiple currencies are
 *                   selected there is no single representative value, so `null`
 *                   is sent until a per-currency payload lands. The legacy
 *                   single `trigger.minBetAmount` is used as a fallback for
 *                   drafts authored before the per-currency inputs existed.
 *    - casino     → the "Wager Amount" input (`trigger.casinoWagerAmount`) —
 *                   the wagered stake required to qualify;
 *    - socials / other → `null` (no wager column applies).
 *  Parsed via `parseBetAmount` (positive number; `null` when blank / invalid /
 *  zero) so a cleared value removes any stored minimum on the BE, and a stale
 *  value from the *other* task type never leaks after a Task Type switch.
 *  Shared by the create and update mappers so both ship the value identically. */
export const resolveMinBetAmount = (draft: TaskDraft): number | null => {
  const trigger = draft.trigger;
  if (isSportsbookTask(draft)) {
    const currencies = trigger.betCurrencies ?? [];
    // Multiple currencies → no single value to ship on the lone column yet.
    if (currencies.length > 1) return null;
    // Exactly one currency → ship that currency's per-currency minimum (falling
    // back to the legacy single value for older drafts).
    if (currencies.length === 1) {
      const perCurrency = trigger.minBetByCurrency?.[currencies[0]];
      return parseBetAmount(perCurrency ?? trigger.minBetAmount);
    }
    // No currency selected → legacy single value only.
    return parseBetAmount(trigger.minBetAmount);
  }
  if (isCasinoTask(draft)) {
    return parseBetAmount(trigger.casinoWagerAmount);
  }
  return null;
};

/** Build a single condition rule from a flat id selection, or `null` when the
 *  selection is empty / omitted (no restriction → no rule emitted). A single
 *  id uses `op: "eq"` with a scalar `value`; multiple ids use `op: "in"` with
 *  an array `value` — matching the Game Pass V2 API guide. */
const buildSelectionRule = (
  field: TaskRuleField,
  selection?: ReadonlyArray<string>,
): TaskRule | null => {
  if (!Array.isArray(selection) || selection.length === 0) return null;
  return selection.length === 1
    ? { field, op: TASK_RULE_OP.EQ, value: selection[0] }
    : { field, op: TASK_RULE_OP.IN, value: [...selection] };
};

/** Encode a Socials task's selections as condition rules (Game Pass V2). The
 *  "Task Type" dropdown picks the social mechanic; the "X Amount" milestone
 *  rides on `target_value` (see the mappers) and, for the streak types, also
 *  on the rule's `gte` value. Mapping (FE-1638 … FE-1641):
 *    - `FOLLOW_X_FOLLOWERS`     → `is_new_relationship eq true`
 *    - `CONSECUTIVE_LOGIN_DAYS` → `consecutive_days gte <X>`
 *    - `P2P_WINS`               → `opponent_account_age_days gte 1`
 *      (anti-farming marker — the only P2P-specific field in the V2 guide)
 *    - `CONSECUTIVE_PICK_DAYS`  → `consecutive_days gte <X>` plus
 *      `shared_device_flag eq true`
 *  The login and pick streaks share `consecutive_days gte X`; the pick variant
 *  additionally emits `shared_device_flag` so the BE (and our reverse mapper)
 *  can tell the two apart — without it they'd be indistinguishable on read.
 *  Only fields from the V2 guide's Social table are emitted so the BE never
 *  sees an `unknown field` (422). The streak rules are omitted when the X
 *  Amount is blank / invalid so we never ship a `consecutive_days gte NaN`. */
export const buildSocialRules = (draft: TaskDraft): TaskRule[] => {
  const { trigger } = draft;
  const socialTaskType: SocialTaskType =
    trigger.socialTaskType ?? DEFAULT_SOCIAL_TASK_TYPE;
  const amount = parseSocialAmount(trigger.socialAmount);
  switch (socialTaskType) {
    case "FOLLOW_X_FOLLOWERS":
      return [
        {
          field: TASK_RULE_FIELD.IS_NEW_RELATIONSHIP,
          op: TASK_RULE_OP.EQ,
          value: true,
        },
      ];
    case "P2P_WINS":
      return [
        {
          field: TASK_RULE_FIELD.OPPONENT_ACCOUNT_AGE_DAYS,
          op: TASK_RULE_OP.GTE,
          value: 1,
        },
      ];
    case "CONSECUTIVE_LOGIN_DAYS":
      return amount !== null
        ? [
            {
              field: TASK_RULE_FIELD.CONSECUTIVE_DAYS,
              op: TASK_RULE_OP.GTE,
              value: amount,
            },
          ]
        : [];
    case "CONSECUTIVE_PICK_DAYS": {
      const pickRules: TaskRule[] = [
        {
          field: TASK_RULE_FIELD.SHARED_DEVICE_FLAG,
          op: TASK_RULE_OP.EQ,
          value: true,
        },
      ];
      if (amount !== null) {
        pickRules.unshift({
          field: TASK_RULE_FIELD.CONSECUTIVE_DAYS,
          op: TASK_RULE_OP.GTE,
          value: amount,
        });
      }
      return pickRules;
    }
    case "USER_REFERRED":
      // Refer-a-user (`user_referred` event) → the referred user making a
      // qualifying deposit. The "X Amount" milestone (number of referrals)
      // rides on `target_value` like the other social types.
      return [
        {
          field: TASK_RULE_FIELD.REFERRED_USER_MADE_QUALIFYING_DEPOSIT,
          op: TASK_RULE_OP.EQ,
          value: true,
        },
      ];
    default:
      return [];
  }
};

/** Encode the Bet Currency multi-select as a `money_type` condition rule (Game
 *  Pass V2). A single currency uses `op: "eq"` with a scalar value; multiple
 *  use `op: "in"` with an array — mirroring `buildSelectionRule`. Returns
 *  `null` when nothing is selected so no rule is emitted. */
const buildMoneyTypeRule = (
  currencies?: ReadonlyArray<BetCurrency>,
): TaskRule | null => {
  if (!currencies || currencies.length === 0) return null;
  const moneyTypes = currencies.map(
    (currency) => BET_CURRENCY_TO_MONEY_TYPE[currency],
  );
  return moneyTypes.length === 1
    ? {
        field: TASK_RULE_FIELD.MONEY_TYPE,
        op: TASK_RULE_OP.EQ,
        value: moneyTypes[0],
      }
    : {
        field: TASK_RULE_FIELD.MONEY_TYPE,
        op: TASK_RULE_OP.IN,
        value: moneyTypes,
      };
};

/** Encode the draft's trigger selections as the BE task `rules` array (Game
 *  Pass V2 — these are no longer sent as top-level columns). The task-type
 *  gate mirrors the modal's per-type fields so a stale selection left over
 *  from a Task Type switch never leaks into the wrong rule set:
 *    - casino task → `provider` / `game_id` rules (from the pickers), an
 *      `outcome` rule (Play / Win — always present, default Play), and an
 *      optional `money_type` rule (the Bet Currency selection). The Action
 *      Amount is NOT a rule — it ships as the task's top-level `target_value`;
 *    - socials task → a social mechanic rule (see `buildSocialRules`);
 *    - sportsbook task → a `sport_id` rule (from the Sport Specific tree); for
 *      a Parlay an `is_parlay eq true` marker plus an optional `num_legs gte`
 *      rule (the Number of Legs input); an optional `odds_american gte` rule
 *      (the Minimum Odds input) and an optional `odds_american lte` rule (the
 *      Maximum Odds input) — both share the `odds_american` field and together
 *      bound the qualifying odds; and a `money_type` rule (the Bet Currency
 *      multi-select). The parlay / odds / money_type conditions live in `rules`
 *      (not as top-level columns) so a rule engine that evaluates `rules`
 *      exclusively still enforces them. (The Minimum Bet Amount is NOT a rule —
 *      it ships on the top-level `min_bet_amount` column.)
 *  A single id uses `op: "eq"`, multiple use `op: "in"`; the numeric
 *  thresholds use `op: "gte"`. Returns `[]` when nothing is selected, which on
 *  update clears any rules the BE had stored. */
export const buildTaskRules = (draft: TaskDraft): TaskRule[] => {
  const { trigger } = draft;
  // Socials tasks carry only their social mechanic rule — no sport / casino /
  // bet-amount rules apply, so return early to keep stale sportsbook/casino
  // selections (never cleared on a Task Type switch) out of the payload.
  if (isSocialTask(draft)) {
    return buildSocialRules(draft);
  }
  const rules: TaskRule[] = [];
  if (isCasinoTask(draft)) {
    const providerRule = buildSelectionRule(
      TASK_RULE_FIELD.PROVIDER,
      trigger.casinoProviders,
    );
    if (providerRule) rules.push(providerRule);
    const gameRule = buildSelectionRule(
      TASK_RULE_FIELD.GAME,
      trigger.casinoGames,
    );
    if (gameRule) rules.push(gameRule);
    // Casino Action (Play / Win) — always present; defaults to Play to match
    // the pill's default state. Shipped on the `outcome` rule field (the BE
    // rejects `casino_action` as an unknown field with a 422). Play maps to
    // the wire value `"settled"` (FE-2005); Win maps to `"WIN"`.
    rules.push({
      field: TASK_RULE_FIELD.OUTCOME,
      op: TASK_RULE_OP.EQ,
      value: CASINO_ACTION_TO_API[trigger.casinoAction ?? "PLAY"],
    });
    // Bet Currency (Rebet Cash / Rebet Coins) → `money_type` rule. A single
    // selection uses `eq`, multiple use `in`. Omitted when nothing is picked.
    const moneyTypeRule = buildSelectionRule(
      TASK_RULE_FIELD.MONEY_TYPE,
      (trigger.casinoBetCurrencies ?? []).map(
        (currency) => BET_CURRENCY_TO_MONEY_TYPE[currency],
      ),
    );
    if (moneyTypeRule) rules.push(moneyTypeRule);
    // NOTE: the casino Action Amount is no longer a rule — it now ships as the
    // task's top-level `target_value` (see the create / update mappers).
    return rules;
  }
  const sportRule = buildSelectionRule(
    TASK_RULE_FIELD.SPORT,
    trigger.sportSpecific,
  );
  if (sportRule) rules.push(sportRule);
  // Leagues selected under a sport → `league_id` rule (tournament id). Falls
  // back to the catalogue `value` when no tournament id exists (static
  // fixtures). Independent of the sport rule — a task may carry a sport, a
  // league, or both.
  const leagueRule = buildSelectionRule(
    TASK_RULE_FIELD.LEAGUE_ID,
    trigger.sportLeagues,
  );
  if (leagueRule) rules.push(leagueRule);
  // Parlay / SGP conditions — the BE expects the leg count (and, for a plain
  // Parlay, the parlay marker) inside `rules` (not as top-level columns) so a
  // rule engine evaluating `rules` enforces them. `is_parlay` stays Parlay-only
  // (SGP carries its own `bet_type=SGP` marker, so it isn't a plain parlay);
  // `num_legs` is emitted for either multi-leg bet type whenever a leg count
  // was entered.
  if (trigger.betType === "PARLAY") {
    rules.push({
      field: TASK_RULE_FIELD.IS_PARLAY,
      op: TASK_RULE_OP.EQ,
      value: true,
    });
  }
  if (betTypeRequiresLegCount(trigger.betType)) {
    const legCount = parseMinLegCount(trigger.minLegCount);
    if (legCount !== null) {
      rules.push({
        field: TASK_RULE_FIELD.NUM_LEGS,
        op: TASK_RULE_OP.GTE,
        value: legCount,
      });
    }
  }
  // Minimum Odds → `odds_american` gte rule (American odds; may be negative).
  // Emitted whenever a value is present so the condition lives in `rules`.
  const minOdds = parseOddsRequirement(trigger.oddsRequirement);
  if (minOdds !== null) {
    rules.push({
      field: TASK_RULE_FIELD.ODDS_AMERICAN,
      op: TASK_RULE_OP.GTE,
      value: minOdds,
    });
  }
  // Maximum Odds → `odds_american` lte rule (same field as Minimum Odds, just
  // the upper bound). A task with both a min and a max ships two
  // `odds_american` rules (gte + lte) that together bound the qualifying odds.
  const maxOdds = parseOddsRequirement(trigger.maxOddsRequirement);
  if (maxOdds !== null) {
    rules.push({
      field: TASK_RULE_FIELD.ODDS_AMERICAN,
      op: TASK_RULE_OP.LTE,
      value: maxOdds,
    });
  }
  // Bet Currency multi-select → `money_type` rule. Omitted when nothing is
  // selected.
  const sportMoneyTypeRule = buildMoneyTypeRule(trigger.betCurrencies);
  if (sportMoneyTypeRule) rules.push(sportMoneyTypeRule);
  // NOTE: the Minimum Bet Amount is no longer a rule — it ships as the task's
  // top-level `min_bet_amount` column (see the create / update mappers).
  return rules;
};

/**
 * Convert the UI's `TaskDraft` into the BE `CreateTaskRequest` body.
 *
 * Field-by-field rationale:
 *   - `season_id` / `challenge_id`: piped in from the caller. The season
 *     id is captured when the wizard creates its draft season; the
 *     challenge id is forwarded once the parent challenge has been
 *     persisted, otherwise it stays `null` so the pre-save local flow
 *     keeps working.
 *   - `challenge` / `challenge_order`: forwarded alongside
 *     `challenge_id` when the caller knows them, otherwise they fall back
 *     to the legacy placeholder values.
 *   - `data_source` ← `trigger.taskType` via `TASK_TYPE_TO_DATA_SOURCE`:
 *     identity for sportsbook / casino, but the socials pill (`"SOCIALS"`)
 *     translates to the singular wire value (`"SOCIAL"`) the BE expects.
 *   - `event_type`: always `null` today. The BE treats `event_type` as a
 *     SOCIAL-only field and rejects it on Sportsbook / Casino tasks
 *     (`event_type is only valid on a SOCIAL task`). The Social event stream
 *     wire value isn't mapped on the FE yet, so every task sends `null`.
 *   - `outcome_required` ← `trigger.qualifier`: mapped through
 *     `QUALIFIER_TO_OUTCOME_REQUIRED`. `null` when the qualifier hasn't
 *     been picked (the modal's submit gate prevents this in practice).
 *   - `bet_type` ← `trigger.betType`: `null` for the default `"ANY"`
 *     pill (matches the example payload's default), the literal enum
 *     value otherwise.
 *   - `target_value` ← `trigger.socialAmount` (socials, via `parseSocialAmount`),
 *     `trigger.casinoActionAmount` (casino, via `parseCasinoActionAmount`), or
 *     `trigger.numberOfBets` (plain sportsbook COUNT, via `parseNumberOfBets`):
 *     parsed to a number. A plain sportsbook task falls back to its
 *     `preservedTargetValue` then `TARGET_VALUE_FALLBACK` when the input is
 *     blank; socials fall back to `TARGET_VALUE_FALLBACK` too when the parse
 *     fails; casino sends `null` when the Action Amount is blank / invalid.
 *     (See `resolveTargetValue`.)
 *   - `min_bet_amount` ← the sportsbook "Minimum Bet Amount" input
 *     (`trigger.minBetAmount`) OR the casino "Wager Amount" input
 *     (`trigger.casinoWagerAmount`), via `resolveMinBetAmount` /
 *     `parseBetAmount`. Top-level column (NOT a rule); `null` for social tasks
 *     or a blank / invalid value.
 *   - `min_odds_american` ← `trigger.oddsRequirement`: the modal's
 *     optional Minimum Odds input. Parsed via `parseOddsRequirement`
 *     (American odds; may be negative; `null` when blank / invalid) so the
 *     value round-trips back into the task preview. (Also emitted as an
 *     `odds_american gte` rule — see `buildTaskRules`.)
 *   - Maximum Odds (`trigger.maxOddsRequirement`) is NOT a top-level column;
 *     it ships exclusively as an `odds_american lte` rule via `buildTaskRules`.
 *   - `rules` ← the trigger selections, encoded as condition rules (Game
 *     Pass V2) via `buildTaskRules`: a `sport_id` rule for sportsbook tasks
 *     (`trigger.sportSpecific`); `provider` / `game_id` rules
 *     (`trigger.casinoProviders` / `trigger.casinoGames`), an `outcome`
 *     rule (`trigger.casinoAction`, default Play) and an optional `money_type`
 *     rule (`trigger.casinoBetCurrencies`) for casino tasks; a social-mechanic
 *     rule for socials tasks (see `buildSocialRules`
 *     — `is_new_relationship` / `consecutive_days` / `opponent_account_age_days`
 *     keyed off `trigger.socialTaskType`). The Minimum Bet Amount is NOT a rule
 *     — it ships on the top-level `min_bet_amount` column. Selection ids use
 *     `op: "eq"` (single) / `op: "in"` (multiple); thresholds use `op: "gte"`.
 *     Empty when nothing is selected. These replace the old top-level
 *     `sports` / `casino_providers` / `casino_games` columns. (The casino
 *     Action Amount ships on `target_value`, not a rule.)
 *   - `min_leg_count` ← `trigger.minLegCount`: the Number of Legs input,
 *     shown for the multi-leg bet types (Parlay / SGP). Parsed via
 *     `parseMinLegCount` (positive integer; `null` when blank / invalid).
 *     Forced to `null` for any bet type that doesn't use a leg count.
 *   - `path`: `"FREE"` constant. Tier selection is per-season (`tiers`
 *     on `SeasonOverviewValues`), not per-task; the BE still requires a
 *     value on every task, so we send the default. A future per-task
 *     tier picker would supply this directly.
 *   - `xp_reward` ← `trigger.xpAmount`: the always-visible XP Amount
 *     input. Parsed via `parseXpReward` (floored integer; `0` when blank
 *     / invalid).
 *   - `completion_mode`, `counting_method`, `completion_condition`,
 *     `exclude_rebet_tokens`, `is_active`: safe constants that match the
 *     example payload. Each becomes a direct mapping when the corresponding
 *     UI lands.
 *
 *   - `counting_method` ← `"STREAK"` when the sportsbook "Streak" card holds a
 *     valid day count, else `"COUNT"` (see `resolveCountingMethod`). A streak
 *     task ships its day count on `target_value` (see `resolveTargetValue`).
 *
 * Intentionally NOT shipped — this trigger field is UI-only today
 * because the BE has no rule for it yet. When the BE adds support,
 * encode it in `buildTaskRules`:
 *   - `trigger.minBetCurrency` (the unit beside Minimum Bet Amount)
 */
export const mapTaskDraftToCreateTaskRequest = (
  draft: TaskDraft,
  context: {
    seasonId: string;
    challengeId?: string | null;
    challengeName?: string;
    /** Zero-based slot of this task *within* its parent challenge — the next
     *  free position (current task count). Stamped as `challenge_order`, the
     *  single ordering key the list sorts by (task analogue of a challenge's
     *  `sort_order`). Drag-reorder later overwrites it. Defaults to 0. */
    challengeOrder?: number;
    /** S3 object key of the task image, resolved by the caller via
     *  `uploadGamePassImage` after presign + PUT. Only included on the wire
     *  when present so a task without an image never sends an empty key. */
    imgKey?: string | null;
  },
): CreateTaskRequest => {
  const trigger = draft.trigger;
  const completionMode =
    resolveTaskCompletionMode(draft) ?? TASK_COMPLETION_MODE.OPEN;
  return {
    season_id: context.seasonId,
    challenge_id: context.challengeId ?? null,
    challenge: context.challengeName?.trim() ?? "",
    challenge_order: context.challengeOrder ?? 0,
    name: draft.name.trim(),
    description: draft.description.trim(),
    ...(context.imgKey ? { img_key: context.imgKey } : {}),
    path: "FREE",
    completion_mode: completionMode,
    data_source: TASK_TYPE_TO_DATA_SOURCE[trigger.taskType],
    // `event_type` is a SOCIAL-only field on the BE — it rejects the request
    // with `event_type is only valid on a SOCIAL task` when it's set on a
    // Sportsbook or Casino task. Sportsbook/Casino therefore send `null`; the
    // Social event stream wire value isn't mapped on the FE yet, so Social
    // sends `null` too until that contract is confirmed.
    event_type: null,
    // `"STREAK"` for a sportsbook Streak task (the BE drives streak logic off
    // `counting_method` + `target_value`); `"COUNT"` for everything else.
    counting_method: resolveCountingMethod(draft),
    completion_condition: "binary",
    // Per-task-type goal (see `resolveTargetValue`): casino → Action Amount;
    // socials → "X Amount" milestone; sportsbook Streak → number of streak
    // days; sportsbook (default) → Number of Bets input (or preserved goal /
    // fallback).
    target_value: resolveTargetValue(draft),
    // Sportsbook-specific fields. Gated on `isSportsbookTask` so casino AND
    // social tasks always send `null` — even if a qualifier / bet-type / odds
    // value was filled in before the admin switched the Task Type pill away
    // from Sportsbook (those draft values are never cleared on switch).
    outcome_required:
      isSportsbookTask(draft) && trigger.qualifier
        ? QUALIFIER_TO_OUTCOME_REQUIRED[trigger.qualifier]
        : null,
    bet_type: isSportsbookTask(draft)
      ? BET_TYPE_TO_API_BET_TYPE[trigger.betType]
      : null,
    min_odds_american: isSportsbookTask(draft)
      ? parseOddsRequirement(trigger.oddsRequirement)
      : null,
    // Maximum Odds is NOT a top-level column — it ships exclusively as an
    // `odds_american` `lte` rule via `buildTaskRules` (the Minimum Odds `gte`
    // rule is its mirror). See the `max_odds_american` notes in the request type.
    // Number of Legs is multi-leg-only (Parlay / SGP) and sportsbook-only —
    // never ship a leg count for a casino / social task or any other bet type,
    // even if a stale value lingers on the draft after a Task Type / bet-type
    // switch.
    min_leg_count:
      isSportsbookTask(draft) && betTypeRequiresLegCount(trigger.betType)
        ? parseMinLegCount(trigger.minLegCount)
        : null,
    // Top-level `min_bet_amount` column (NOT a rule). Sourced per task type by
    // `resolveMinBetAmount`: the sportsbook "Minimum Bet Amount" or the casino
    // "Wager Amount". `null` for social tasks (and when blank / invalid / zero)
    // so a stale value never ships after a Task Type switch.
    min_bet_amount: resolveMinBetAmount(draft),
    exclude_rebet_tokens: false,
    xp_reward: parseXpReward(trigger.xpAmount),
    // All trigger selections — Sport Specific, casino provider / game, casino
    // Action (outcome) and casino Bet Currency (money_type) — are encoded as
    // condition rules (Game Pass V2) instead of top-level columns. (The casino
    // Action Amount ships on `target_value`, and the Minimum Bet Amount on
    // `min_bet_amount`, above.) `buildTaskRules` gates by task type, so a stale
    // selection from a Task Type switch never leaks into the wrong rule set.
    rules: buildTaskRules(draft),
    is_active: true,
    // Per-task sportsbook / casino submission mode ships as `completion_mode`
    // (`OPEN` = Auto, `USER_CHOICE` = Manual), NOT `task_submission_mode`.
  };
};

export {
  resolveTaskCompletionMode,
  TASK_COMPLETION_MODE,
} from "../../components/GamePass/CreateSeason/createSeasonData";

const postCreateTask = async (
  payload: CreateTaskRequest,
): Promise<TaskApiRecord | null> => {
  // `getPrivateInstance()` already bakes the baseURL (from the
  // `REACT_APP_ADMIN_PANEL_BACKEND_BASE_URL` env) and the bearer token
  // (via a request interceptor in `PrivateAdminPanelInstance.js`) into
  // the axios instance, so the call site stays as terse as
  // `useGetSeasonsList.ts`: pass the centralised route + the body, no
  // host prefix, no header munging.
  const response = await getPrivateInstance().post<CreateTaskResponse>(
    API_ROUTES.CREATE_TASK,
    // Body is shipped as-is — no `operator_id` augmentation (see the
    // module-level note above for why).
    payload,
  );
  // Tolerant unwrap (FE-1692): the POST already succeeded, so an
  // unrecognised / missing body must not be treated as a failure. Returns
  // `null` when no task payload is present; the caller still treats it as a
  // successful create (with an unknown server id) instead of erroring.
  return unwrapGamePassEntityOrNull(response.data, "task");
};

/**
 * React Query mutation for `POST /game-pass-admin/v2/admin/tasks`.
 *
 * Uses the shared `getPrivateInstance()` builder, so the
 * staff-token + baseURL plumbing lives in one place (matches
 * `useGetSeasonsList` exactly). On success it invalidates the parent
 * season's detail query AND the seasons list so any consumer reading
 * the season's task list — or per-card `task_count` metric — refetches
 * with the new task.
 */
export const useCreateTask = (): UseMutationResult<
  TaskApiRecord | null,
  Error,
  CreateTaskRequest
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["game-pass", "tasks", "create"],
    mutationFn: postCreateTask,
    onSuccess: (_task, variables) => {
      // Refetch the season's task listing so the challenge card's Tasks
      // dropdown shows the new task immediately — without this the
      // in-place editor stays stale until a remount (navigate away + back).
      queryClient.invalidateQueries({
        queryKey: ["game-pass", "tasks", "list", variables.season_id],
      });
      // Refresh both the season's detail view (which carries task counts
      // / nested tasks once that endpoint is wired) and the seasons list
      // (challenge_count / task_count are surfaced as metrics on the
      // card and need to refresh as soon as a task is added).
      queryClient.invalidateQueries({
        queryKey: ["game-pass", "seasons", "detail", variables.season_id],
      });
      queryClient.invalidateQueries({
        queryKey: ["game-pass", "seasons", "list"],
      });
    },
  });
};
